import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  InputAdornment,
  Radio,
  RadioGroup,
  FormLabel,
  FormControlLabel,
  Divider,
  Autocomplete,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  People as PeopleIcon,
  CardGiftcard as GuestPassIcon,
  TrendingUp as ConversionIcon,
  Dashboard as DashboardIcon,
  RestartAlt as ResetIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Group as GroupIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import nonMemberService from '../api/services/nonMemberService';
import guestPassService from '../api/services/guestPassService';
import conversionService from '../api/services/conversionService';
import adminAnalyticsService from '../api/services/adminAnalyticsService';
import memberService from '../api/services/memberService';
import axiosInstance from '../api/axiosConfig';
import {
  NonMember,
  GuestPass,
  ConversionApplication,
  AdminDashboard,
  GuestPassStatus,
  Member,
} from '../types';

interface NonMemberSettings {
  default_guest_annual_limit: number;
  visit_reset_type: 'calendar_year' | 'registration_anniversary';
  allow_guest_pass_when_at_limit: boolean;
  require_sponsor_for_visit: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const NonMemberManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dashboard data
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null);

  // Non-members data
  const [nonMembers, setNonMembers] = useState<NonMember[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [interestFilter, setInterestFilter] = useState<string>('');

  // Guest passes data
  const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
  const [passFilter, setPassFilter] = useState<string>('today');

  // Conversion applications data
  const [applications, setApplications] = useState<ConversionApplication[]>([]);
  const [applicationFilter, setApplicationFilter] = useState<string>('pending');

  // Dialog state
  const [selectedNonMember, setSelectedNonMember] = useState<NonMember | null>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [newInterestLevel, setNewInterestLevel] = useState('');
  const [interestNotes, setInterestNotes] = useState('');

  // Issue Pass Dialog state
  const [issuePassDialogOpen, setIssuePassDialogOpen] = useState(false);
  const [issuePassLoading, setIssuePassLoading] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<NonMember | null>(null);
  const [selectedSponsor, setSelectedSponsor] = useState<Member | null>(null);
  const [sponsorSearchQuery, setSponsorSearchQuery] = useState('');
  const [sponsorOptions, setSponsorOptions] = useState<Member[]>([]);
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [guestFeeAmount, setGuestFeeAmount] = useState<string>('');
  const [feeBilledTo, setFeeBilledTo] = useState<string>('sponsor');
  const [passNotes, setPassNotes] = useState('');

  // Available facilities (could come from API/config)
  const availableFacilities = [
    'Tennis Courts',
    'Swimming Pool',
    'Golf Course',
    'Fitness Center',
    'Dining Room',
    'Clubhouse',
  ];

  // Settings state
  const [settings, setSettings] = useState<NonMemberSettings>({
    default_guest_annual_limit: 6,
    visit_reset_type: 'calendar_year',
    allow_guest_pass_when_at_limit: false,
    require_sponsor_for_visit: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const response = await adminAnalyticsService.getDashboard();
      setDashboard(response.data);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
    }
  }, []);

  // Load non-members
  const loadNonMembers = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (interestFilter) filters.conversion_interest = interestFilter;

      const response = searchQuery
        ? await nonMemberService.searchNonMembers(searchQuery, true)
        : await nonMemberService.getAllNonMembers(true, filters);

      setNonMembers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load non-members');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, interestFilter]);

  // Load guest passes
  const loadGuestPasses = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (passFilter) {
        case 'today':
          response = await guestPassService.getTodaysPasses();
          break;
        case 'pending':
          response = await guestPassService.getPendingCheckin();
          break;
        case 'active':
          response = await guestPassService.getActivePasses();
          break;
        case 'upcoming':
          response = await guestPassService.getUpcomingPasses();
          break;
        default:
          response = await guestPassService.getAll();
      }
      setGuestPasses(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load guest passes');
    } finally {
      setLoading(false);
    }
  }, [passFilter]);

  // Load conversion applications
  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (applicationFilter) {
        case 'pending':
          response = await conversionService.getPendingReview();
          break;
        case 'waitlist':
          response = await conversionService.getWaitlist();
          break;
        case 'ready':
          response = await conversionService.getReadyForConversion();
          break;
        default:
          response = await conversionService.getAll({ status: applicationFilter });
      }
      setApplications(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [applicationFilter]);

  // Load settings
  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await axiosInstance.get('/non-members/settings');
      if (response.data.data) {
        setSettings(response.data.data);
      }
    } catch (err: any) {
      // If settings don't exist yet, use defaults
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || 'Failed to load settings');
      }
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Save settings
  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    try {
      await axiosInstance.put('/non-members/settings', settings);
      setSuccess('Settings saved successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Apply settings to all existing non-members
  const handleApplyToAll = async () => {
    if (!window.confirm(`Are you sure you want to update ALL existing non-members to ${settings.default_guest_annual_limit} guest visits per year?`)) {
      return;
    }
    setApplying(true);
    setError(null);
    try {
      const response = await axiosInstance.post('/non-members/settings/apply-to-all');
      setSuccess(response.data.message || 'Settings applied to all non-members');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply settings');
    } finally {
      setApplying(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Load data when tab changes
  useEffect(() => {
    switch (activeTab) {
      case 0:
        loadDashboard();
        break;
      case 1:
        loadNonMembers();
        break;
      case 2:
        loadGuestPasses();
        // Also load non-members for the Issue Pass dialog
        if (nonMembers.length === 0) {
          loadNonMembers();
        }
        break;
      case 3:
        loadApplications();
        break;
      case 4:
        loadSettings();
        break;
    }
  }, [activeTab, loadDashboard, loadNonMembers, loadGuestPasses, loadApplications, loadSettings]);

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Handle block/unblock
  const handleBlock = async () => {
    if (!selectedNonMember) return;
    setError(null);
    try {
      await adminAnalyticsService.blockNonMember(selectedNonMember.id, blockReason);
      setSuccess('Non-member blocked successfully');
      setBlockDialogOpen(false);
      setBlockReason('');
      loadNonMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to block non-member');
    }
  };

  const handleUnblock = async (member: NonMember) => {
    setError(null);
    try {
      await adminAnalyticsService.unblockNonMember(member.id);
      setSuccess('Non-member unblocked successfully');
      loadNonMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to unblock non-member');
    }
  };

  // Handle interest level update
  const handleUpdateInterest = async () => {
    if (!selectedNonMember) return;
    setError(null);
    try {
      await adminAnalyticsService.updateInterestLevel(
        selectedNonMember.id,
        newInterestLevel,
        interestNotes
      );
      setSuccess('Interest level updated successfully');
      setInterestDialogOpen(false);
      setNewInterestLevel('');
      setInterestNotes('');
      loadNonMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update interest level');
    }
  };

  // Handle export
  const handleExport = async (type: 'non-members' | 'guest-passes' | 'applications') => {
    try {
      let blob: Blob;
      let filename: string;
      switch (type) {
        case 'non-members':
          blob = await adminAnalyticsService.exportNonMembersCSV();
          filename = 'non_members_export.csv';
          break;
        case 'guest-passes':
          blob = await adminAnalyticsService.exportGuestPassesCSV();
          filename = 'guest_passes_export.csv';
          break;
        case 'applications':
          blob = await adminAnalyticsService.exportApplicationsCSV();
          filename = 'applications_export.csv';
          break;
      }
      adminAnalyticsService.downloadCSV(blob, filename);
      setSuccess('Export downloaded successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to export');
    }
  };

  // Handle guest visit reset
  const handleResetGuestVisits = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to reset this non-member\'s guest visits to 0?')) return;
    setError(null);
    try {
      await adminAnalyticsService.resetGuestVisitsForMember(memberId);
      setSuccess('Guest visits reset successfully');
      loadNonMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset guest visits');
    }
  };

  // Handle status toggle (active/inactive)
  const handleToggleStatus = async (member: NonMember) => {
    const currentStatus = member.membership_status?.toUpperCase();
    const newStatus = currentStatus === 'NON_MEMBER_ACTIVE' ? 'NON_MEMBER_INACTIVE' : 'NON_MEMBER_ACTIVE';
    const newStatusLabel = newStatus === 'NON_MEMBER_ACTIVE' ? 'active' : 'inactive';

    if (!window.confirm(`Are you sure you want to set ${member.first_name} ${member.last_name} to ${newStatusLabel}?`)) return;

    setError(null);
    try {
      await adminAnalyticsService.updateStatus(member.id, newStatus as 'NON_MEMBER_ACTIVE' | 'NON_MEMBER_INACTIVE');
      setSuccess(`Non-member set to ${newStatusLabel}`);
      loadNonMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  // Guest pass check-in/out
  const handleCheckIn = async (passId: string) => {
    try {
      await guestPassService.checkIn(passId);
      setSuccess('Guest checked in successfully');
      loadGuestPasses();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check in guest');
    }
  };

  const handleCheckOut = async (passId: string) => {
    try {
      await guestPassService.checkOut(passId);
      setSuccess('Guest checked out successfully');
      loadGuestPasses();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check out guest');
    }
  };

  // Search for sponsors (members)
  const handleSponsorSearch = async (query: string) => {
    if (query.length < 2) {
      setSponsorOptions([]);
      return;
    }
    setSponsorLoading(true);
    try {
      const response = await memberService.searchMembersForBooking(query);
      setSponsorOptions(response.data || []);
    } catch (err) {
      console.error('Failed to search members:', err);
      setSponsorOptions([]);
    } finally {
      setSponsorLoading(false);
    }
  };

  // Reset issue pass dialog
  const resetIssuePassDialog = () => {
    setSelectedGuest(null);
    setSelectedSponsor(null);
    setSponsorSearchQuery('');
    setSponsorOptions([]);
    setVisitDate(new Date().toISOString().split('T')[0]);
    setSelectedFacilities([]);
    setGuestFeeAmount('');
    setFeeBilledTo('sponsor');
    setPassNotes('');
  };

  // Handle issue guest pass
  const handleIssuePass = async () => {
    if (!selectedGuest || !selectedSponsor) {
      setError('Please select both a guest and a sponsor');
      return;
    }
    if (!visitDate) {
      setError('Please select a visit date');
      return;
    }

    setIssuePassLoading(true);
    setError(null);
    try {
      await guestPassService.create({
        guest_member_id: selectedGuest.id,
        sponsor_member_id: selectedSponsor.id,
        visit_date: visitDate,
        facilities_allowed: selectedFacilities.length > 0 ? selectedFacilities : undefined,
        guest_fee_amount: guestFeeAmount ? parseFloat(guestFeeAmount) : undefined,
        fee_billed_to: feeBilledTo || undefined,
        notes: passNotes || undefined,
      });
      setSuccess('Guest pass issued successfully');
      setIssuePassDialogOpen(false);
      resetIssuePassDialog();
      loadGuestPasses();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to issue guest pass');
    } finally {
      setIssuePassLoading(false);
    }
  };

  // Get status chip color
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status?.toLowerCase()) {
      case 'non_member_active':
      case 'active':
      case 'completed':
      case 'converted':
      case 'approved':
        return 'success';
      case 'non_member_blocked':
      case 'blocked':
      case 'rejected':
      case 'cancelled':
        return 'error';
      case 'non_member_inactive':
      case 'inactive':
      case 'expired':
        return 'default';
      case 'pending':
      case 'submitted':
      case 'issued':
        return 'info';
      case 'under_review':
      case 'waitlisted':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getInterestColor = (level: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (level?.toLowerCase()) {
      case 'high':
      case 'ready_to_apply':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  // Render Dashboard Tab
  const renderDashboard = () => (
    <Grid container spacing={3}>
      {/* Summary Cards */}
      <Grid size={{ xs: 12, md: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Total Non-Members</Typography>
            <Typography variant="h4">{dashboard?.non_member_summary?.total_non_members || 0}</Typography>
            <Typography variant="body2" color="success.main">
              {dashboard?.non_member_summary?.active_non_members || 0} active
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Today's Guest Passes</Typography>
            <Typography variant="h4">{dashboard?.today?.guest_passes_today || 0}</Typography>
            <Typography variant="body2" color="primary.main">
              {dashboard?.today?.currently_active || 0} currently active
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Pending Applications</Typography>
            <Typography variant="h4">{dashboard?.action_items?.pending_applications || 0}</Typography>
            <Typography variant="body2" color="warning.main">
              {dashboard?.action_items?.waitlist_count || 0} on waitlist
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>Conversions</Typography>
            <Typography variant="h4">{dashboard?.conversion_funnel?.converted || 0}</Typography>
            <Typography variant="body2" color="info.main">
              this period
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Conversion Funnel */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Conversion Funnel</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Non-Members</TableCell>
                  <TableCell align="right">{dashboard?.conversion_funnel?.non_members || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Expressed Interest</TableCell>
                  <TableCell align="right">{dashboard?.conversion_funnel?.expressed_interest || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Applications Submitted</TableCell>
                  <TableCell align="right">{dashboard?.conversion_funnel?.applications_submitted || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Under Review</TableCell>
                  <TableCell align="right">{dashboard?.conversion_funnel?.under_review || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Approved</TableCell>
                  <TableCell align="right">{dashboard?.conversion_funnel?.approved_pending_payment || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Converted</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{dashboard?.conversion_funnel?.converted || 0}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>

      {/* Guest Pass Summary */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Guest Pass Summary</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Total Passes</TableCell>
                  <TableCell align="right">{dashboard?.guest_pass_summary?.total || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Issued (Pending)</TableCell>
                  <TableCell align="right">{dashboard?.guest_pass_summary?.issued || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Active (Checked In)</TableCell>
                  <TableCell align="right">{dashboard?.guest_pass_summary?.active || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Completed</TableCell>
                  <TableCell align="right">{dashboard?.guest_pass_summary?.completed || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Cancelled</TableCell>
                  <TableCell align="right">{dashboard?.guest_pass_summary?.cancelled || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>No Show</TableCell>
                  <TableCell align="right">{dashboard?.guest_pass_summary?.no_show || 0}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Actions */}
      <Grid size={{ xs: 12 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('non-members')}>
                Export Non-Members
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('guest-passes')}>
                Export Guest Passes
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('applications')}>
                Export Applications
              </Button>
              <Button variant="outlined" color="secondary" startIcon={<SettingsIcon />} onClick={() => setActiveTab(4)}>
                Settings
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Render Non-Members Tab
  const renderNonMembers = () => (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by name, email, phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadNonMembers()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="NON_MEMBER_ACTIVE">Active</MenuItem>
            <MenuItem value="NON_MEMBER_INACTIVE">Inactive</MenuItem>
            <MenuItem value="NON_MEMBER_BLOCKED">Blocked</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Interest</InputLabel>
          <Select
            value={interestFilter}
            label="Interest"
            onChange={(e) => setInterestFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="ready_to_apply">Ready to Apply</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadNonMembers}>
          Refresh
        </Button>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExport('non-members')}>
          Export
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Interest</TableCell>
              <TableCell>Guest Visits</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nonMembers.map((member) => (
              <TableRow key={member.id}>
                <TableCell>{member.first_name} {member.last_name}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                  <Chip
                    label={member.membership_status?.replace('NON_MEMBER_', '')}
                    color={getStatusColor(member.membership_status || '')}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.conversion_interest_level || 'none'}
                    color={getInterestColor(member.conversion_interest_level || '')}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {member.guest_visits_this_year || 0} / {member.guest_annual_limit || 6}
                </TableCell>
                <TableCell>{member.acquisition_source || '-'}</TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => navigate(`/non-members/${member.id}`)}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Update Interest">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedNonMember(member);
                        setNewInterestLevel(member.conversion_interest_level || 'none');
                        setInterestDialogOpen(true);
                      }}
                    >
                      <ConversionIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reset Guest Visits">
                    <IconButton
                      size="small"
                      onClick={() => handleResetGuestVisits(member.id)}
                      disabled={(member.guest_visits_this_year || 0) === 0}
                    >
                      <ResetIcon />
                    </IconButton>
                  </Tooltip>
                  {member.membership_status?.toUpperCase() !== 'NON_MEMBER_BLOCKED' && (
                    <Tooltip title={member.membership_status?.toUpperCase() === 'NON_MEMBER_ACTIVE' ? 'Set Inactive' : 'Set Active'}>
                      <IconButton
                        size="small"
                        color={member.membership_status?.toUpperCase() === 'NON_MEMBER_ACTIVE' ? 'success' : 'default'}
                        onClick={() => handleToggleStatus(member)}
                      >
                        {member.membership_status?.toUpperCase() === 'NON_MEMBER_ACTIVE' ? <ActiveIcon /> : <InactiveIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {member.membership_status?.toUpperCase() === 'NON_MEMBER_BLOCKED' ? (
                    <Tooltip title="Unblock">
                      <IconButton size="small" color="success" onClick={() => handleUnblock(member)}>
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Block">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedNonMember(member);
                          setBlockDialogOpen(true);
                        }}
                      >
                        <BlockIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {nonMembers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No non-members found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  // Render Guest Passes Tab
  const renderGuestPasses = () => (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={passFilter}
            label="Filter"
            onChange={(e) => setPassFilter(e.target.value)}
          >
            <MenuItem value="today">Today's Passes</MenuItem>
            <MenuItem value="pending">Pending Check-In</MenuItem>
            <MenuItem value="active">Currently Active</MenuItem>
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="all">All Passes</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadGuestPasses}>
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            if (nonMembers.length === 0) {
              loadNonMembers();
            }
            setIssuePassDialogOpen(true);
          }}
        >
          Issue Pass
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Pass Code</TableCell>
              <TableCell>Guest</TableCell>
              <TableCell>Sponsor</TableCell>
              <TableCell>Visit Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Check-In</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {guestPasses.map((pass) => (
              <TableRow key={pass.id}>
                <TableCell><code>{pass.pass_code}</code></TableCell>
                <TableCell>
                  {pass.guest?.first_name} {pass.guest?.last_name}
                </TableCell>
                <TableCell>
                  {pass.sponsor?.first_name} {pass.sponsor?.last_name}
                </TableCell>
                <TableCell>{pass.visit_date}</TableCell>
                <TableCell>
                  <Chip
                    label={pass.status}
                    color={getStatusColor(pass.status as string)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {pass.check_in_time ? new Date(pass.check_in_time).toLocaleTimeString() : '-'}
                </TableCell>
                <TableCell>
                  {pass.status === GuestPassStatus.ISSUED && (
                    <Button size="small" variant="contained" onClick={() => handleCheckIn(pass.id)}>
                      Check In
                    </Button>
                  )}
                  {pass.status === GuestPassStatus.ACTIVE && (
                    <Button size="small" variant="outlined" onClick={() => handleCheckOut(pass.id)}>
                      Check Out
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {guestPasses.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No guest passes found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  // Render Applications Tab
  const renderApplications = () => (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={applicationFilter}
            label="Filter"
            onChange={(e) => setApplicationFilter(e.target.value)}
          >
            <MenuItem value="pending">Pending Review</MenuItem>
            <MenuItem value="waitlist">Waitlist</MenuItem>
            <MenuItem value="ready">Ready for Conversion</MenuItem>
            <MenuItem value="CONVERTED">Converted</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadApplications}>
          Refresh
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Application #</TableCell>
              <TableCell>Applicant</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested Tier</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map((app) => (
              <TableRow key={app.id}>
                <TableCell><code>{app.application_number}</code></TableCell>
                <TableCell>
                  {app.applicant?.first_name} {app.applicant?.last_name}
                  <Typography variant="caption" display="block" color="textSecondary">
                    {app.applicant?.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={app.status?.replace(/_/g, ' ')}
                    color={getStatusColor(app.status as string)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{app.requested_tier?.tier_name || '-'}</TableCell>
                <TableCell>
                  {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/conversion-applications/${app.id}`)}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {applications.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No applications found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  // Render Settings Tab
  const renderSettings = () => (
    <>
      {settingsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon /> Guest Visit Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure the default settings for non-member guest visits. These settings apply to new non-members.
                Existing non-members can have their limits adjusted individually.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Default Annual Guest Visit Limit"
                  type="number"
                  value={settings.default_guest_annual_limit}
                  onChange={(e) => setSettings({ ...settings, default_guest_annual_limit: parseInt(e.target.value) || 0 })}
                  helperText="The maximum number of guest visits allowed per year for new non-members"
                  inputProps={{ min: 0, max: 100 }}
                  sx={{ maxWidth: 300 }}
                />

                <Divider />

                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                    Visit Counter Reset Period
                  </FormLabel>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose when the guest visit counter resets to zero
                  </Typography>
                  <RadioGroup
                    value={settings.visit_reset_type}
                    onChange={(e) => setSettings({ ...settings, visit_reset_type: e.target.value as 'calendar_year' | 'registration_anniversary' })}
                  >
                    <FormControlLabel
                      value="calendar_year"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">Calendar Year</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Resets on January 1st each year
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="registration_anniversary"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">Registration Anniversary</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Resets on the anniversary of when the non-member registered
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                <Divider />

                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Current Configuration Summary
                  </Typography>
                  <Typography variant="body2">
                    New non-members will be allowed <strong>{settings.default_guest_annual_limit}</strong> guest visits per year,
                    resetting on {settings.visit_reset_type === 'calendar_year' ? 'January 1st' : 'their registration anniversary'}.
                  </Typography>
                </Paper>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon /> Apply to Existing Non-Members
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The default settings above only apply to newly registered non-members.
                Use this button to update all existing non-members to match the current default guest visit limit.
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will overwrite any individually customized guest visit limits.
              </Alert>
              <Button
                variant="outlined"
                color="warning"
                startIcon={applying ? <CircularProgress size={20} color="inherit" /> : <GroupIcon />}
                onClick={handleApplyToAll}
                disabled={applying}
              >
                {applying ? 'Applying...' : `Apply ${settings.default_guest_annual_limit} Visits to All Existing Non-Members`}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Non-Member Management</Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<PeopleIcon />} label="Non-Members" />
          <Tab icon={<GuestPassIcon />} label="Guest Passes" />
          <Tab icon={<ConversionIcon />} label="Applications" />
          <Tab icon={<SettingsIcon />} label="Settings" />
        </Tabs>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <TabPanel value={activeTab} index={0}>{renderDashboard()}</TabPanel>
          <TabPanel value={activeTab} index={1}>{renderNonMembers()}</TabPanel>
          <TabPanel value={activeTab} index={2}>{renderGuestPasses()}</TabPanel>
          <TabPanel value={activeTab} index={3}>{renderApplications()}</TabPanel>
          <TabPanel value={activeTab} index={4}>{renderSettings()}</TabPanel>
        </>
      )}

      {/* Block Dialog */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)}>
        <DialogTitle>Block Non-Member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to block{' '}
            <strong>{selectedNonMember?.first_name} {selectedNonMember?.last_name}</strong>?
          </Typography>
          <TextField
            fullWidth
            label="Reason for blocking"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBlock} color="error" variant="contained">
            Block
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interest Level Dialog */}
      <Dialog open={interestDialogOpen} onClose={() => setInterestDialogOpen(false)}>
        <DialogTitle>Update Interest Level</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Update conversion interest level for{' '}
            <strong>{selectedNonMember?.first_name} {selectedNonMember?.last_name}</strong>
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Interest Level</InputLabel>
            <Select
              value={newInterestLevel}
              label="Interest Level"
              onChange={(e) => setNewInterestLevel(e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="ready_to_apply">Ready to Apply</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Notes"
            value={interestNotes}
            onChange={(e) => setInterestNotes(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterestDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateInterest} color="primary" variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue Guest Pass Dialog */}
      <Dialog
        open={issuePassDialogOpen}
        onClose={() => {
          setIssuePassDialogOpen(false);
          resetIssuePassDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Issue Guest Pass</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Guest Selection */}
            <Autocomplete
              options={nonMembers}
              value={selectedGuest}
              onChange={(_, newValue) => setSelectedGuest(newValue)}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
              renderInput={(params) => (
                <TextField {...params} label="Guest (Non-Member)" required />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {/* Sponsor Selection */}
            <Autocomplete
              options={sponsorOptions}
              value={selectedSponsor}
              onChange={(_, newValue) => setSelectedSponsor(newValue)}
              onInputChange={(_, newInputValue) => {
                setSponsorSearchQuery(newInputValue);
                handleSponsorSearch(newInputValue);
              }}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name}${option.member_number ? ` (#${option.member_number})` : ''}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sponsor (Member)"
                  required
                  helperText="Type to search members"
                />
              )}
              loading={sponsorLoading}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(x) => x}
            />

            {/* Visit Date */}
            <TextField
              label="Visit Date"
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />

            {/* Facilities */}
            <FormControl fullWidth>
              <InputLabel>Facilities Allowed</InputLabel>
              <Select
                multiple
                value={selectedFacilities}
                onChange={(e) => setSelectedFacilities(e.target.value as string[])}
                label="Facilities Allowed"
                renderValue={(selected) => selected.join(', ')}
              >
                {availableFacilities.map((facility) => (
                  <MenuItem key={facility} value={facility}>
                    <Checkbox checked={selectedFacilities.indexOf(facility) > -1} />
                    <ListItemText primary={facility} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Guest Fee */}
            <TextField
              label="Guest Fee Amount"
              type="number"
              value={guestFeeAmount}
              onChange={(e) => setGuestFeeAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />

            {/* Fee Billed To */}
            <FormControl fullWidth>
              <InputLabel>Fee Billed To</InputLabel>
              <Select
                value={feeBilledTo}
                onChange={(e) => setFeeBilledTo(e.target.value)}
                label="Fee Billed To"
              >
                <MenuItem value="sponsor">Sponsor</MenuItem>
                <MenuItem value="guest">Guest</MenuItem>
                <MenuItem value="waived">Waived</MenuItem>
              </Select>
            </FormControl>

            {/* Notes */}
            <TextField
              label="Notes"
              multiline
              rows={2}
              value={passNotes}
              onChange={(e) => setPassNotes(e.target.value)}
            />

            {/* Guest Visit Info */}
            {selectedGuest && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Guest Visit Status
                </Typography>
                <Typography variant="body2">
                  {selectedGuest.first_name} has used{' '}
                  <strong>{selectedGuest.guest_visits_this_year || 0}</strong> of{' '}
                  <strong>{selectedGuest.guest_annual_limit || 6}</strong> annual visits.
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIssuePassDialogOpen(false);
              resetIssuePassDialog();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleIssuePass}
            color="primary"
            variant="contained"
            disabled={issuePassLoading || !selectedGuest || !selectedSponsor}
          >
            {issuePassLoading ? <CircularProgress size={24} /> : 'Issue Pass'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NonMemberManagementPage;
